import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { PhoneIcon } from "@heroicons/react/24/outline";

import * as API from "../../api";
import list from "../../api/list.json";

/**
 * Phone verification — premium redesign. Logic preserved verbatim: two-step
 * flow (phone -> code), 4-request rate limit per step, 20s resend countdown,
 * "I already have a code" / "I received no code" escape hatches, redirect
 * query param on success.
 */
const PhoneConferm = () => {
  const location = useLocation();
  const history = useHistory();
  const redirection = new URLSearchParams(location.search).get("redirect");
  const { enqueueSnackbar } = useSnackbar();
  const maxRequest = 4;

  const [countPhoneSend, setCountPhoneSend] = React.useState(0);
  const [countCodeSend, setCountCodeSend] = React.useState(0);
  const [page, setPage] = React.useState("phone"); // 'phone' or 'conferm'
  const [loading, setLoading] = React.useState(false);
  const [phoneHelper, setPhoneHelper] = React.useState("");
  const [codeHelper, setCodeHelper] = React.useState("");
  const [countdown, setCountdown] = React.useState(0);
  const [resetCode, setResetCode] = React.useState(false);
  const [isoCode, setIsoCode] = React.useState("+212");

  const submitPhone = (event) => {
    event.preventDefault();
    setPhoneHelper("");

    const formData = new FormData(event.target);
    formData.set("phone", isoCode + formData.get("phone").replace(/\D/g, ""));

    setCountPhoneSend(countPhoneSend + 1);
    if (countPhoneSend > maxRequest) {
      setPhoneHelper("Vous avez dépassé la limite d'opérations, réessayez plus tard");
      return;
    }
    setLoading(true);
    API.SignNumber(formData)
      .then(() => {
        setPage("conferm");
        event.target.reset();
      })
      .catch((err) => {
        if (err.status === 422) setPhoneHelper(err.data.errors.phone);
        else if (err.status === 500) setPhoneHelper("Erreur du serveur, vous pouvez réessayer plus tard");
        else setPhoneHelper(err.data.errors);
      })
      .then(() => setLoading(false));
  };

  const submitCode = (event) => {
    event.preventDefault();
    setCodeHelper("");

    const formData = new FormData(event.target);
    formData.set("verification_code", formData.get("verification_code").replace(/\D/g, ""));

    setCountCodeSend(countCodeSend + 1);
    if (countCodeSend > maxRequest) {
      setCodeHelper("Vous avez dépassé la limite d'opérations, réessayez plus tard");
      return;
    }
    setLoading(true);
    API.ConfermNumber(formData)
      .then(() => {
        history.push(redirection ? `/${redirection}` : "edit");
        setPage("phone");
        event.target.reset();
        enqueueSnackbar("Le numéro de téléphone a été mis à jour avec succès", { variant: "success" });
      })
      .catch((err) => {
        if (err.status === 422) setCodeHelper(err.data.errors.verification_code);
        else if (err.status === 500) setCodeHelper("Erreur du serveur, vous pouvez réessayer plus tard");
        else setCodeHelper(err.data.errors);
      })
      .then(() => setLoading(false));
  };

  const ProgressCountdown = (timeleft) => {
    setResetCode(false);
    return new Promise((resolve) => {
      const countdownTimer = setInterval(() => {
        timeleft--;
        setCountdown(timeleft);
        if (timeleft <= 0) {
          clearInterval(countdownTimer);
          resolve(true);
        }
      }, 1000);
    });
  };

  React.useEffect(() => {
    if (page === "conferm" && !resetCode) {
      ProgressCountdown(20).then(() => setResetCode(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, 0]);

  return (
    <section className="mx-auto max-w-md rounded-2xl border border-slate-100 bg-white p-5 shadow-card sm:p-6">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-500">
          <PhoneIcon className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-display text-lg font-semibold text-ink">
            Confirmation de votre numéro
          </h2>
          <p className="text-xs text-ink-subtle">
            {page === "phone"
              ? "Un code de vérification vous sera envoyé par SMS"
              : "Entrez le code reçu par SMS"}
          </p>
        </div>
      </div>

      {page === "phone" ? (
        <form onSubmit={submitPhone} method="POST" className="mt-6 space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Numéro de téléphone</label>
            <div className="flex gap-2">
              <select
                value={isoCode}
                onChange={(e) => setIsoCode(e.target.value)}
                disabled={loading}
                className="w-28 shrink-0 rounded-xl border border-slate-200 bg-white px-2.5 py-2.5 text-sm text-ink focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              >
                {list.map((c) => (
                  <option key={c.isoCode} value={c.dialCode}>
                    {c.dialCode} {c.isoCode}
                  </option>
                ))}
              </select>
              <div className="relative flex-1">
                <input
                  name="phone"
                  type="tel"
                  placeholder="6 12 34 56 78"
                  disabled={loading}
                  onFocus={(e) => e.target.select()}
                  className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-3.5 pr-10 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50 rtl:pl-10 rtl:pr-3.5"
                />
                <PhoneIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle rtl:right-auto rtl:left-3" />
              </div>
            </div>
            {phoneHelper ? <p className="mt-1 text-xs text-rose-500">{phoneHelper}</p> : null}
          </div>

          <button
            type="button"
            onClick={() => {
              setPage("conferm");
              setResetCode(true);
            }}
            className="text-xs font-medium text-brand-500 hover:underline"
          >
            J'ai déjà un code
          </button>

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center rounded-xl bg-accent-500 px-4 py-3 text-sm font-bold text-brand-900 shadow-card transition-all duration-200 hover:bg-accent-400 hover:shadow-card-hover disabled:opacity-60"
          >
            {loading ? (
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-brand-900/30 border-t-brand-900" />
            ) : (
              "Confirmer"
            )}
          </button>
          <p className="text-center text-xs text-ink-subtle">
            Vous recevrez un SMS contenant le code de vérification
          </p>
        </form>
      ) : (
        <form onSubmit={submitCode} method="POST" className="mt-6 space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Code de vérification</label>
            <input
              name="verification_code"
              type="text"
              inputMode="numeric"
              placeholder="0000"
              disabled={loading}
              onFocus={(e) => e.target.select()}
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-center text-lg font-semibold tracking-[0.5em] text-ink placeholder:tracking-normal placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50"
            />
            {codeHelper ? <p className="mt-1 text-xs text-rose-500">{codeHelper}</p> : null}
          </div>

          {resetCode ? (
            <button
              type="button"
              onClick={() => setPage("phone")}
              className="text-xs font-medium text-brand-500 hover:underline"
            >
              Je n'ai reçu aucun code
            </button>
          ) : (
            <p className="text-xs text-ink-subtle">Le code est valide pendant {countdown} secondes</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center rounded-xl bg-accent-500 px-4 py-3 text-sm font-bold text-brand-900 shadow-card transition-all duration-200 hover:bg-accent-400 hover:shadow-card-hover disabled:opacity-60"
          >
            {loading ? (
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-brand-900/30 border-t-brand-900" />
            ) : (
              "Confirmer"
            )}
          </button>
        </form>
      )}
    </section>
  );
};

export default PhoneConferm;
